import { CommonModule } from '@angular/common';
import { Component, Output, EventEmitter, inject } from '@angular/core';
import { Router } from '@angular/router';
import { SubscriptionComparisonComponent } from '../../subscription/subscription-comparison/subscription-comparison.component';
import { MixpanelService } from 'src/app/core/services/mixpanel/mixpanel.service';
import { SubscriptionService } from 'src/app/core/services/subscription/subscription.service';
import { ModalService } from 'src/app/core/services/modal/modal.service';

/**
 * FeatureLockedModalComponent
 *
 * Modal that displays when a user tries to access a Game Master-only feature.
 * Shows a custom message explaining the feature is locked, followed by the
 * subscription comparison table and upgrade button.
 *
 * @example
 * <app-feature-locked-modal />
 */
@Component({
  selector: 'app-feature-locked-modal',
  standalone: true,
  imports: [CommonModule, SubscriptionComparisonComponent],
  templateUrl: './feature-locked-modal.component.html',
  styleUrls: ['./feature-locked-modal.component.scss'],
})
export class FeatureLockedModalComponent {
  private mixPanelService = inject(MixpanelService);
  private subscriptionService = inject(SubscriptionService);
  private router = inject(Router);
  private modalService = inject(ModalService);

  /**
   * Whether the modal is open
   */
  isOpen = false;

  /**
   * Custom message to display (explaining which feature is locked)
   */
  featureMessage = '';

  /**
   * Emits when the modal should be closed
   */
  @Output() close = new EventEmitter<void>();

  /**
   * Emits when the user clicks the upgrade button
   */
  @Output() upgrade = new EventEmitter<void>();

  constructor() {
    // Subscribe to modal service event
    this.modalService.showFeatureLockedModal.subscribe((featureName: string) => {
      this.open(featureName);
    });
  }

  /**
   * Opens the modal with a custom feature message
   */
  open(featureName: string): void {
    this.featureMessage = `You must become a <span class="game-master-highlight">Game Master</span> to use this feature, become a a Game Master now!`;
    this.isOpen = true;
    this.mixPanelService.trackEvent('feature_locked_modal_opened', {
      feature: featureName,
    });
  }

  /**
   * Closes the modal
   */
  onClose(): void {
    this.isOpen = false;
    this.close.emit();
    this.mixPanelService.trackEvent('feature_locked_modal_closed');
  }

  /**
   * Handles the upgrade button click
   */
  onUpgrade(): void {
    this.mixPanelService.trackEvent('feature_locked_modal_upgrade_clicked');

    // Trigger upgrade flow
    this.subscriptionService.upgradeToGameMaster();

    // Emit to parent component (in case they want custom handling)
    this.upgrade.emit();

    // Close the modal
    this.onClose();
  }
}
